import { useEffect, useRef, useState } from "react";
import { ArrowRight, Bell } from "lucide-react";
import DiscoverRentals from "../../pages/DiscoverRentals";
import RentalProfile, { ReviewsSection } from "../../pages/RentalProfile";
import CustomerConfirmation from "../../pages/CustomerConfirmation";
import CustomerBookingDetail from "../../pages/CustomerBookingDetail";
import ReviewInvite from "../../pages/ReviewInvite";
import PublicVehicleDetailModal from "../minisite/PublicVehicleDetailModal";
import { PublicHeader, PublicMobileDrawer } from "../PublicLayout";
import HomeHero from "../HomeHero";
import useAccessibleOverlay from "../../hooks/useAccessibleOverlay";
import DemoSandbox from "./DemoSandbox";
import Spotlight from "./Spotlight";
import ChatSimulation from "./ChatSimulation";
import BrandOutro from "./BrandOutro";
import WarningCard from "../warnings/WarningCard";
import ReassignVehicleModal from "../ReassignVehicleModal";
import { NotepadPanel, DraftPanel, BookingPanel, ConfirmationSentPanel } from "../../pages/promoter/demos/DemoPanels";
import { inquiryScript, confirmationLinkMessage, reviewLinkMessage } from "./chatScript";
import { RENTER_STEPS } from "./steps";
import { INCIDENT_CHAT } from "./incidentScenario";

/**
 * Renders exactly one step's screen, in whichever document it's mounted in
 * (inline in the parent page for Desktop mode, inside the phone iframe's
 * own document for Phone mode: see DeviceFrame.jsx). Because Spotlight and
 * DemoSandbox both operate on `containerRef`, guidance and click/submit
 * interception are always local to wherever this component actually lives,
 * so both modes get identical, fully-working behavior.
 */
export default function RenterWalkthroughStage({ stepIndex, demoState, deviceMode, onEvent, reducedMotion, resetToken }) {
  const containerRef = useRef(null);
  const step = RENTER_STEPS[stepIndex];
  const [bookAgainPreview, setBookAgainPreview] = useState(null);
  const [confirmationViewedVehicle, setConfirmationViewedVehicle] = useState(false);
  const [homeDrawerOpen, setHomeDrawerOpen] = useState(false);
  const [businessVehicleModalOpen, setBusinessVehicleModalOpen] = useState(false);
  const [businessVehicleViewed, setBusinessVehicleViewed] = useState(false);
  const [submittedReview, setSubmittedReview] = useState(null);
  const [issueBusinessPhase, setIssueBusinessPhase] = useState("notify");
  const chatPhasesPlayedRef = useRef({ inquiry: false, confirmation: false, review: false });

  useEffect(() => { setBookAgainPreview(null); }, [stepIndex]);
  useEffect(() => { setConfirmationViewedVehicle(false); }, [stepIndex, resetToken]);
  useEffect(() => { setHomeDrawerOpen(false); }, [stepIndex, resetToken, deviceMode]);
  useEffect(() => { setBusinessVehicleModalOpen(false); setBusinessVehicleViewed(false); }, [stepIndex, resetToken]);
  useEffect(() => { chatPhasesPlayedRef.current = { inquiry: false, confirmation: false, review: false }; }, [resetToken]);
  useEffect(() => { setSubmittedReview(null); }, [resetToken]);
  useEffect(() => { setIssueBusinessPhase("notify"); }, [stepIndex, resetToken]);

  function navigate(action, payload) {
    // "Book again" is a real <Link> intercepted like everything else, but
    // here the honest, informative response is to show what it *would* do
    // (real prefilled destination) rather than silently swallowing it or
    // bubbling it up to change the outer step.
    if (step.key === "book-again" && action === "link") {
      setBookAgainPreview(payload);
      advance();
      return;
    }
    // The Home step's real "Discover Rentals" link, whether clicked
    // directly in the desktop nav or inside the real mobile drawer: is the
    // one required interaction that should actually advance the walkthrough.
    if (step.key === "home" && action === "link" && payload === "/rentals") {
      setHomeDrawerOpen(false);
      advance();
      return;
    }
    onEvent?.({ type: "navigate", action, payload, stepKey: step.key });
  }
  function submit(action, form) {
    // The real inquiry form (RentalProfile's "Send inquiry") is a genuine
    // way into the conversation: submitting it should feel the same as
    // the walkthrough's own "continue" affordance.
    if (action === "rp-inquiry-form" && step.key === "business") {
      advance();
      return;
    }
    onEvent?.({ type: "submit", action, form, stepKey: step.key });
  }
  function advance() {
    onEvent?.({ type: "advance" });
  }

  const subState = { confirmationViewedVehicle, homeDrawerOpen, businessVehicleModalOpen, businessVehicleViewed, submittedReview, issueBusinessPhase };
  const targetTestId = resolveSpotlightTarget(step, demoState, deviceMode, subState);
  const substep = resolveSubstepCopy(step, demoState, deviceMode, subState);

  // The left-side explanation panel lives in the parent page (outside this
  // iframe), so the current substep has to be reported up: see
  // ProductWalkthrough.jsx's WalkthroughPlayer, which falls back to the
  // step's own static description/points whenever this posts null (i.e. for
  // every single-interaction step, where the static copy already says enough).
  useEffect(() => {
    window.parent.postMessage({ type: "WALKTHROUGH_SUBSTEP", stepKey: step.key, substep }, window.location.origin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.key, substep?.heading, substep?.instruction, substep?.context]);

  return (
    <div ref={containerRef} className="relative" data-testid="walkthrough-stage" data-step={step.key}>
      <DemoSandbox
        testId="walkthrough-sandbox"
        onNavigate={(href) => navigate("link", href)}
        onSubmit={(form) => submit(form.getAttribute("data-testid") || "unknown-form", form)}
        passthroughSubmitTestIds={["review-invite-form"]}
      >
        <StageScreen
          step={step}
          demoState={demoState}
          onAdvance={advance}
          onNavigate={navigate}
          reducedMotion={reducedMotion}
          bookAgainPreview={bookAgainPreview}
          chatPhasesPlayedRef={chatPhasesPlayedRef}
          confirmationViewedVehicle={confirmationViewedVehicle}
          onViewedVehicle={() => setConfirmationViewedVehicle(true)}
          homeDrawerOpen={homeDrawerOpen}
          onHomeDrawerOpenChange={setHomeDrawerOpen}
          onBusinessVehicleModalOpenChange={(open) => {
            setBusinessVehicleModalOpen(open);
            if (!open) setBusinessVehicleViewed(true);
          }}
          businessVehicleViewed={businessVehicleViewed}
          submittedReview={submittedReview}
          onReviewSubmitted={setSubmittedReview}
          deviceMode={deviceMode}
          issueBusinessPhase={issueBusinessPhase}
          onIssueBusinessPhase={setIssueBusinessPhase}
        />
      </DemoSandbox>
      {targetTestId && (
        <Spotlight
          containerRef={containerRef}
          targetTestId={targetTestId}
          label={resolveSpotlightLabel(step, deviceMode, subState)}
          reducedMotion={reducedMotion}
        />
      )}
    </div>
  );
}

function resolveSpotlightTarget(step, demoState, deviceMode, subState) {
  if (step.key === "business") {
    if (subState.businessVehicleModalOpen) return step.spotlight.modalClose;
    if (!subState.businessVehicleViewed) return demoState.vehicle ? `rp-vehicle-view-details-${demoState.vehicle.id}` : null;
    return step.spotlight.continue;
  }
  if (step.key === "review-shown") {
    return subState.submittedReview ? `rp-review-${subState.submittedReview.id}` : null;
  }
  if (step.key === "issue-business-fix") {
    if (subState.issueBusinessPhase === "notify" || subState.issueBusinessPhase === "idle") return "warning-notify-demo-warning-1";
    if (subState.issueBusinessPhase === "notify-form") return "warning-notify-form-demo-warning-1-submit";
    return "reassign-choose-smart";
  }
  if (!step.spotlight) return null;
  switch (step.key) {
    case "home":
      if (deviceMode !== "phone") return step.spotlight.desktop;
      return subState.homeDrawerOpen ? step.spotlight.phoneDrawer : step.spotlight.phone;
    case "discover":
      return `rental-card-${demoState.company.id}`;
    case "confirmation":
      return subState.confirmationViewedVehicle ? "confirm-booking-btn" : "confirmation-view-vehicle-btn";
    default:
      return step.spotlight.both || null;
  }
}

function resolveSpotlightLabel(step, deviceMode, subState) {
  if (step.key === "business") {
    if (subState.businessVehicleModalOpen) return step.spotlight.modalCloseTooltip;
    if (!subState.businessVehicleViewed) return step.spotlight.vehicleTooltip;
    return step.spotlight.continueTooltip;
  }
  if (step.key === "issue-business-fix") {
    // "idle" happens if the notify form is opened then closed without
    // submitting: same real starting point as "notify", so the spotlight
    // (and its label) correctly return to the Notify button either way.
    if (subState.issueBusinessPhase === "notify" || subState.issueBusinessPhase === "idle") return "Notify the renter";
    if (subState.issueBusinessPhase === "notify-form") return "Send the notice";
    return "Choose the replacement vehicle";
  }
  if (step.key === "review-shown") return step.spotlight?.tooltip;
  if (step.key === "confirmation") {
    return subState.confirmationViewedVehicle ? "Confirm this booking" : "First, check the vehicle details";
  }
  if (step.key === "home" && deviceMode === "phone") {
    return subState.homeDrawerOpen ? step.spotlight.phoneDrawerTooltip : step.spotlight.phoneTooltip;
  }
  if (step.key === "home") return step.spotlight.desktopTooltip;
  return step.spotlight?.tooltip;
}

/**
 * The left-side explanation must track what's actually happening right now,
 * not just which outer step is active: several outer steps (home, business,
 * confirmation, review-shown) contain more than one real interaction before
 * the step itself advances. Returns null for single-interaction steps (the
 * outer step's own static description/points in steps.js already say enough,
 * so there is no need to duplicate it here); returns {heading, instruction, context}
 * for steps whose guidance must change as the visitor progresses through
 * them. Shares the exact same phase-detection conditions as
 * resolveSpotlightTarget/resolveSpotlightLabel above (by design: the
 * highlighted control and the instruction describing it must never disagree
 * about which phase is current).
 */
function resolveSubstepCopy(step, demoState, deviceMode, subState) {
  if (step.key === "home") {
    if (deviceMode === "phone" && !subState.homeDrawerOpen) {
      return {
        heading: "Open the menu",
        instruction: "Tap the menu icon (top right) to open navigation.",
        context: "This is the real ConfirmPH homepage, shown as a phone would see it.",
      };
    }
    if (deviceMode === "phone" && subState.homeDrawerOpen) {
      return {
        heading: "Open Discover Rentals",
        instruction: "Tap Discover Rentals in the menu.",
        context: "This is the real navigation drawer, same as the live site.",
      };
    }
    return {
      heading: "Open Discover Rentals",
      instruction: "Click Discover Rentals in the navigation.",
      context: "This is the real ConfirmPH homepage: renters start here to browse listed rental businesses.",
    };
  }
  if (step.key === "business") {
    if (subState.businessVehicleModalOpen) {
      return {
        heading: "Review the vehicle",
        instruction: "Close this panel when you're done: tap the X or outside it.",
        context: "The real vehicle-detail modal, same one renters see in production.",
      };
    }
    if (!subState.businessVehicleViewed) {
      return {
        heading: "Open the rental listing",
        instruction: `Select the highlighted ${demoState.vehicle?.name || "vehicle"}.`,
        context: "This is the real public rental profile, where renters see a company's vehicles and booking information.",
      };
    }
    return {
      heading: "Contact the business",
      instruction: "Tap Continue to see how you'd reach this business.",
      context: "The real contact card, same as production.",
    };
  }
  if (step.key === "confirmation") {
    if (!subState.confirmationViewedVehicle) {
      return {
        heading: "Check the vehicle first",
        instruction: "Tap the vehicle to review its condition before confirming.",
        context: "The real confirmation page shows any existing damage recorded on the vehicle.",
      };
    }
    return {
      heading: "Confirm your booking",
      instruction: "Tap Confirm to move the booking forward.",
      context: "Confirming here is what a renter does to lock in the real booking.",
    };
  }
  if (step.key === "review-shown") {
    if (!subState.submittedReview) return null;
    return {
      heading: "Your review is live",
      instruction: "Nothing to click here: tap Next below when you're ready to continue.",
      context: "Your demo review now appears on the same business page future renters would see.",
    };
  }
  if (step.key === "issue-business-fix") {
    if (subState.issueBusinessPhase === "notify" || subState.issueBusinessPhase === "idle") {
      return {
        heading: "Notify the renter",
        instruction: "Tap Notify on the warning card.",
        context: "The real Warning Center card: staff see the exact same one for any affected booking.",
      };
    }
    if (subState.issueBusinessPhase === "notify-form") {
      return {
        heading: "Send the notice",
        instruction: "Tap Send to notify Anna.",
        context: "Only a customer-safe notice goes out: no private incident details.",
      };
    }
    return {
      heading: "Find a replacement",
      instruction: "Choose the highlighted replacement vehicle.",
      context: "The real Reassign Vehicle modal: the same safety/availability checks as production.",
    };
  }
  return null;
}

function StageScreen({
  step, demoState, onAdvance, onNavigate, reducedMotion, bookAgainPreview, chatPhasesPlayedRef,
  confirmationViewedVehicle, onViewedVehicle, homeDrawerOpen, onHomeDrawerOpenChange,
  onBusinessVehicleModalOpenChange, businessVehicleViewed, submittedReview, onReviewSubmitted, deviceMode,
  issueBusinessPhase, onIssueBusinessPhase,
}) {
  switch (step.screen) {
    case "home":
      return <HomeScreen drawerOpen={homeDrawerOpen} onDrawerOpenChange={onHomeDrawerOpenChange} />;
    case "discover":
      return <DiscoverScreen demoState={demoState} onAdvance={onAdvance} />;
    case "business":
      if (step.key === "review-shown") {
        return <ReviewedBusinessScreen demoState={demoState} submittedReview={submittedReview} />;
      }
      return (
        <BusinessScreen
          demoState={demoState}
          onVehicleModalOpenChange={onBusinessVehicleModalOpenChange}
          showContinue={businessVehicleViewed}
          onAdvance={onAdvance}
        />
      );
    case "business-booking":
      return <BusinessBookingScreen demoState={demoState} onAdvance={onAdvance} />;
    case "chat":
      return (
        <ChatPhaseScreen
          step={step}
          demoState={demoState}
          reducedMotion={reducedMotion}
          chatPhasesPlayedRef={chatPhasesPlayedRef}
          onAdvance={onAdvance}
        />
      );
    case "confirmation":
      return (
        <ConfirmationScreen
          demoState={demoState}
          onAdvance={onAdvance}
          viewedVehicle={confirmationViewedVehicle}
          onViewedVehicle={onViewedVehicle}
        />
      );
    case "confirmed":
      return <CustomerBookingDetail preloaded={demoState.confirmedPreloaded} />;
    case "pickup-receive":
      return <PickupReceiveScreen demoState={demoState} onAdvance={onAdvance} />;
    case "ongoing":
      return <CustomerBookingDetail preloaded={demoState.ongoingPreloaded} />;
    case "completed":
      return <CustomerBookingDetail preloaded={demoState.completedPreloaded} />;
    case "book-again":
      return <BookAgainScreen demoState={demoState} preview={bookAgainPreview} />;
    case "review":
      return <ReviewScreen demoState={demoState} onAdvance={onAdvance} onReviewSubmitted={onReviewSubmitted} />;
    case "notification":
      return <NotificationScreen step={step} onAdvance={onAdvance} deviceMode={deviceMode} />;
    case "outro":
      return <OutroScreen deviceMode={deviceMode} />;
    case "issue-notice":
      return <AtRiskScreen demoState={demoState} onAdvance={onAdvance} />;
    case "issue-chat":
      return <IssueChatScreen reducedMotion={reducedMotion} onAdvance={onAdvance} />;
    case "issue-business-fix":
      return <BusinessFixScreen demoState={demoState} phase={issueBusinessPhase} onPhase={onIssueBusinessPhase} onAdvance={onAdvance} />;
    case "issue-change-review":
      return <ChangeReviewScreen demoState={demoState} onAdvance={onAdvance} />;
    case "renter-reviews":
      return <RenterReviewsChangeScreen demoState={demoState} onAdvance={onAdvance} />;
    case "reconfirmed":
      return <CustomerBookingDetail preloaded={demoState.reconfirmedPreloaded} />;
    default:
      return null;
  }
}

function HomeScreen({ drawerOpen, onDrawerOpenChange }) {
  const { panelRef, triggerRef } = useAccessibleOverlay(drawerOpen, () => onDrawerOpenChange(false));
  return (
    <div className="bg-[#FAFAFA]" data-testid="walkthrough-home-screen">
      <PublicHeader drawerOpen={drawerOpen} triggerRef={triggerRef} onOpenDrawer={() => onDrawerOpenChange(true)} />
      <PublicMobileDrawer drawerOpen={drawerOpen} onClose={() => onDrawerOpenChange(false)} panelRef={panelRef} />
      <HomeHero />
    </div>
  );
}

function DiscoverScreen({ demoState, onAdvance }) {
  const card = companyToCard(demoState.company);
  return <DiscoverRentals demoItems={[card]} onSelectCompany={onAdvance} />;
}

function companyToCard(c) {
  return {
    id: c.id, public_slug: c.public_slug, name: c.name, city: c.city, is_demo: c.is_demo,
    is_enterprise: false, public_website_enabled: false, rating_avg: c.rating_avg, rating_count: c.rating_count,
    status_badge: c.online_booking_enabled ? "Online booking available" : "Uses ConfirmPH",
    response_label: null, vehicle_categories: (c.vehicles || []).map((v) => v.category).filter(Boolean).slice(0, 4),
    starting_price_php: (c.vehicles || []).reduce((min, v) => (v.price_per_day && (!min || v.price_per_day < min) ? v.price_per_day : min), null),
    description: c.public_description_full, public_logo_url: c.public_logo_url,
  };
}

/**
 * The real business page: vehicle card + real vehicle-detail modal + real
 * contact card are all part of this SAME real page in production (the
 * vehicle detail is a modal, not a separate page), so the guided sequence
 * (view vehicle → close → contact) stays on this one real screen instead of
 * inventing standalone "vehicle details" / "contact" pages. Once the vehicle
 * has been viewed, a small Continue affordance appears below the real page
 * content (the real contact card itself has no "click to proceed": this is
 * the walkthrough's own, clearly-separate nudge, same pattern already used
 * elsewhere in this walkthrough).
 */
function BusinessScreen({ demoState, onVehicleModalOpenChange, showContinue, onAdvance }) {
  return (
    <div data-testid="walkthrough-business-screen">
      <RentalProfile preloaded={demoState.company} onVehicleModalOpenChange={onVehicleModalOpenChange} />
      {showContinue && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 -mt-2">
          <button
            type="button"
            onClick={onAdvance}
            data-testid="walkthrough-business-continue"
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            See how this conversation usually goes <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/** Reuses the exact same real business page, now with the just-submitted
 * demo review folded into its real Reviews section (never sent to the real
 * database: purely local to this render). */
function ReviewedBusinessScreen({ demoState, submittedReview }) {
  const reviews = submittedReview ? [submittedReview, ...(demoState.company.reviews || [])] : (demoState.company.reviews || []);
  const count = (demoState.company.rating_count || 0) + (submittedReview ? 1 : 0);
  const avg = submittedReview
    ? ((demoState.company.rating_avg || 0) * (demoState.company.rating_count || 0) + submittedReview.rating) / count
    : demoState.company.rating_avg;
  return (
    <div data-testid="walkthrough-review-shown-screen">
      <ReviewsSection reviews={reviews} avg={avg || submittedReview?.rating || 0} count={count} />
    </div>
  );
}

/**
 * Reuses the real staff-side demo panels already built for the promoter
 * onboarding demos (`pages/promoter/demos/DemoPanels.jsx`) instead of
 * inventing a new "business records booking" UI: same simplified,
 * demo-safe presentation, just fed this walkthrough's own local data.
 */
function BusinessBookingScreen({ demoState, onAdvance }) {
  const v = demoState.vehicle;
  const booking = demoState.confirmationPreloaded.booking;
  const start = formatDemoDate(booking.start_datetime);
  const end = formatDemoDate(booking.end_datetime);
  const price = `₱${Number(demoState.price).toLocaleString()}`;
  const deposit = `₱${Number(demoState.deposit).toLocaleString()}`;
  const balance = `₱${Number(demoState.price - demoState.deposit).toLocaleString()}`;

  const notepadData = {
    customer: booking.customer_name, contact: booking.customer_phone, vehicle: v?.name,
    pickup: start, pickup_location: booking.pickup_location, ret: end, price, notes: "Agreed via Messenger",
  };
  const draftData = { customer: booking.customer_name, vehicle: v?.name, pickup: start, ret: end, price, subtotal: price, deposit, balance, source: "Messenger inquiry", status: "Draft" };
  const bookingData = { ...draftData, status: "Awaiting customer confirmation" };

  return (
    <div className="max-w-xl mx-auto py-6 px-4 space-y-3" data-testid="walkthrough-business-booking-screen">
      <NotepadPanel title="Inquiry recorded" data={notepadData} />
      <DraftPanel data={draftData} />
      <BookingPanel data={bookingData} />
      <ConfirmationSentPanel />
      <button
        type="button"
        onClick={onAdvance}
        data-testid="walkthrough-business-booking-continue"
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#1B4332] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#143027]"
      >
        Back to the conversation <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function formatDemoDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) +
    " " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/**
 * The one conversation thread, revisited three times without ever
 * re-animating what's already been read: see chatScript.js for the shared
 * script content.
 */
function ChatPhaseScreen({ step, demoState, reducedMotion, chatPhasesPlayedRef, onAdvance }) {
  const vehicleName = demoState.vehicle?.name || "vehicle";
  const businessName = demoState.company.name;
  const inquiry = inquiryScript(vehicleName, businessName);
  const confirmMsg = confirmationLinkMessage(businessName, `confirmph.app/c/${demoState.bookingRef}`);
  const reviewMsg = reviewLinkMessage(businessName, `confirmph.app/review/${demoState.bookingRef}`);

  const phase = step.chatPhase;
  let staticMessages = [];
  let script = [];
  let linkCard = null;
  let continueLabel = null;

  if (phase === "inquiry") {
    script = inquiry;
    continueLabel = "Continue";
  } else if (phase === "confirmation") {
    staticMessages = inquiry;
    linkCard = confirmMsg;
  } else if (phase === "review") {
    staticMessages = [...inquiry, { from: "business", text: confirmMsg.staticText }];
    linkCard = reviewMsg;
  }

  return (
    <div className="max-w-md mx-auto py-6 px-4">
      <ChatSimulation
        staticMessages={staticMessages}
        script={script}
        linkCard={linkCard}
        continueLabel={continueLabel}
        onLinkClick={onAdvance}
        onContinue={onAdvance}
        reducedMotion={reducedMotion}
        alreadyPlayed={chatPhasesPlayedRef.current[phase]}
        onFullyPlayed={() => { chatPhasesPlayedRef.current[phase] = true; }}
      />
    </div>
  );
}

/**
 * The one genuinely new renter-specific screen with no existing real UI to
 * reuse: comparing the vehicle against what ConfirmPH already has on
 * record (the same demo damage entries the confirmation step shows) before
 * accepting it.
 */
function PickupReceiveScreen({ demoState, onAdvance }) {
  const damages = demoState.confirmationPreloaded.damages || [];
  return (
    <div className="max-w-md mx-auto py-8 px-4" data-testid="walkthrough-pickup-receive-screen">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Before you accept the vehicle</div>
        <ul className="mt-3 space-y-2.5 text-sm text-slate-700">
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1B4332] mt-1.5 flex-none" />
            <span>Recorded damage: {damages.length ? damages.map((d) => d.tag_code.replace(/_/g, " ")).join(", ") : "none recorded"}: check it matches what you see</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1B4332] mt-1.5 flex-none" />
            <span>Odometer reading matches what's on file</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1B4332] mt-1.5 flex-none" />
            <span>Fuel or charge level matches what's on file</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1B4332] mt-1.5 flex-none" />
            <span>Keys and any included items are handed over</span>
          </li>
        </ul>
      </div>
      <button
        type="button"
        onClick={onAdvance}
        data-testid="walkthrough-pickup-continue"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#1B4332] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#143027]"
      >
        I've checked the vehicle <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * The real confirmation page (Confirm / Request change / Confirm buttons -
 * all real), plus a real "View vehicle details" affordance that opens the
 * same real vehicle-detail modal used on the business page. The intended
 * order: check the vehicle first, then confirm: is guided by the
 * spotlight (see resolveSpotlightTarget above), not enforced: either button
 * is always clickable.
 */
function ConfirmationScreen({ demoState, onAdvance, viewedVehicle, onViewedVehicle }) {
  const [preloaded, setPreloaded] = useState(demoState.confirmationPreloaded);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  function onAction(action, payload) {
    if (action === "confirm") {
      setTimeout(() => onAdvance(), 1400);
      return { status: "confirmed" };
    }
    if (action === "acknowledge_damages") {
      return { damages_acknowledged_at: new Date().toISOString(), damages_acknowledged_by: preloaded.booking.customer_name };
    }
    return { status: "change_requested" };
  }
  return (
    <div data-testid="walkthrough-confirmation-screen">
      <CustomerConfirmation
        preloaded={preloaded}
        onAction={onAction}
        onViewVehicleDetails={() => setShowVehicleModal(true)}
      />
      {showVehicleModal && demoState.vehicle && (
        <PublicVehicleDetailModal
          companyId={demoState.company.id}
          vehicle={demoState.vehicle}
          onClose={() => { setShowVehicleModal(false); if (!viewedVehicle) onViewedVehicle(); }}
        />
      )}
    </div>
  );
}

function BookAgainScreen({ demoState, preview }) {
  return (
    <div data-testid="walkthrough-book-again-screen">
      <CustomerBookingDetail preloaded={demoState.completedPreloaded} />
      {preview && (
        <div className="max-w-3xl mx-auto px-4 pb-8 -mt-2">
          <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="walkthrough-book-again-preview">
            <div className="font-semibold">In real life, this would open:</div>
            <div className="mt-1 font-mono text-xs break-all">{preview}</div>
            <p className="mt-2 text-emerald-900/80">
              Prefilled with your name, email, phone, and this vehicle: a new booking request, not a duplicate charge.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewScreen({ demoState, onAdvance, onReviewSubmitted }) {
  function onSubmit({ rating, title, comment }) {
    onReviewSubmitted({
      id: "demo-review-1",
      customer_name_display: "Alex R.",
      rating,
      title: title || null,
      comment,
      verified: true,
      created_at: new Date().toISOString(),
      business_reply: null,
    });
    setTimeout(() => onAdvance(), 1600);
  }
  return <ReviewInvite preloaded={demoState.reviewPreloaded} onSubmit={onSubmit} />;
}

function NotificationScreen({ step, onAdvance, deviceMode }) {
  // `lockScreen` is opt-in per step (see steps.js): only the two incident
  // push-style beats (issue-notification, issue-updated-notification) use
  // the phone lock-screen treatment, and only in phone mode - a lock screen
  // has no desktop equivalent, so desktop keeps the existing in-page card.
  // Ordinary reminders (pickup-reminder, return-transition) are untouched.
  if (step.lockScreen && deviceMode === "phone") {
    return <LockScreenNotification step={step} onAdvance={onAdvance} />;
  }
  return (
    <div className="max-w-sm mx-auto py-10 px-4" data-testid="walkthrough-notification-screen">
      <p className="text-center text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-2">{step.eyebrow}</p>
      <button
        type="button"
        onClick={onAdvance}
        data-testid="walkthrough-notification-continue"
        className="w-full text-left rounded-2xl bg-white border border-slate-200 shadow-sm p-3.5 flex items-start gap-3 hover:bg-slate-50"
      >
        <div className="w-8 h-8 rounded-lg bg-[#1B4332] text-white grid place-items-center flex-none">
          <Bell className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-semibold text-slate-900">ConfirmPH</span>
            <span className="text-[10px] text-slate-400 flex-none">now</span>
          </div>
          <div className="text-sm font-medium text-slate-900 truncate">{step.notification?.title}</div>
          <p className="text-xs text-slate-500 mt-0.5">{step.notification?.body}</p>
        </div>
      </button>
      <p className="mt-3 text-center text-[11px] text-slate-400">Simulated notification: tap it to continue</p>
    </div>
  );
}

/**
 * Phone lock-screen mockup for the two incident push-style notification
 * beats. Purely presentational chrome (time/date/wallpaper) around the
 * SAME {title, body} content the in-page notification card already uses:
 * this never invents separate notification copy, it just stages the first
 * beat of the incident story as a normal phone would show it, cold open,
 * before the renter has even opened the app. Deliberately still labeled
 * "Simulated notification" so nobody mistakes this for a claim that
 * ConfirmPH sends renters real push notifications today (it doesn't: see
 * PART 1 audit - no customer push subscription exists).
 */
function LockScreenNotification({ step, onAdvance }) {
  const now = new Date();
  const time = now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const date = now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  return (
    <div
      className="h-full min-h-[560px] w-full flex flex-col bg-gradient-to-b from-[#1B4332] via-[#0d2c20] to-[#04120c] text-white px-5 pt-16 pb-8"
      data-testid="walkthrough-lockscreen"
    >
      <div className="text-center">
        <div className="text-6xl font-light tracking-tight tabular-nums">{time}</div>
        <div className="mt-1 text-sm text-white/70">{date}</div>
      </div>
      <div className="flex-1" />
      <button
        type="button"
        onClick={onAdvance}
        data-testid="walkthrough-notification-continue"
        className="w-full text-left rounded-2xl bg-white/95 backdrop-blur shadow-xl p-3.5 flex items-start gap-3 hover:bg-white transition-colors"
      >
        <div className="w-8 h-8 rounded-lg bg-[#1B4332] text-white grid place-items-center flex-none">
          <Bell className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-semibold text-slate-900">ConfirmPH</span>
            <span className="text-[10px] text-slate-400 flex-none">now</span>
          </div>
          <div className="text-sm font-medium text-slate-900 truncate">{step.notification?.title}</div>
          <p className="text-xs text-slate-500 mt-0.5">{step.notification?.body}</p>
        </div>
      </button>
      <p className="mt-3 text-center text-[11px] text-white/50">Simulated notification: tap it to continue</p>
    </div>
  );
}

/** Device-dependent outro video: the very last slide of the walkthrough
 * (both the normal flow's own "outro" step and the issue flow's matching
 * final step share this same "outro" screen dispatch). */
function OutroScreen({ deviceMode }) {
  return <BrandOutro deviceMode={deviceMode} />;
}

/** Small, clearly-labeled banner so it's obvious a screen is showing the
 * BUSINESS/staff perspective rather than the renter's own view: the real
 * staff app itself (DashboardLayout) isn't reused here, same simplified-
 * staff-view precedent as BusinessBookingScreen above. */
function StaffPreviewFrame({ children }) {
  return (
    <div data-testid="walkthrough-staff-frame">
      <div className="bg-[#0F1F18] text-emerald-100 text-[11px] font-semibold uppercase tracking-[0.16em] text-center py-1.5">
        ConfirmPH: staff view (preview)
      </div>
      <div className="p-4 sm:p-5 bg-[#F6F8F7]">{children}</div>
    </div>
  );
}

function IssueChatScreen({ reducedMotion, onAdvance }) {
  return (
    <div className="max-w-md mx-auto py-6 px-4" data-testid="walkthrough-issue-chat-screen">
      <ChatSimulation
        script={INCIDENT_CHAT.affectedRenter}
        continueLabel="Continue"
        onContinue={onAdvance}
        reducedMotion={reducedMotion}
      />
    </div>
  );
}

function BusinessFixScreen({ demoState, phase, onPhase, onAdvance }) {
  if (phase === "reassign") {
    return (
      <StaffPreviewFrame>
        <ReassignVehicleModal
          bookingId={demoState.issueBookingId || demoState.bookingId}
          open
          onClose={() => onPhase("notify")}
          onDone={onAdvance}
          preloaded={demoState.replacementPreloaded}
          onSubmit={async () => ({ new_vehicle: { name: demoState.replacementVehicle.name } })}
        />
      </StaffPreviewFrame>
    );
  }
  return (
    <StaffPreviewFrame>
      <div className="max-w-lg mx-auto" data-testid="walkthrough-business-fix-screen">
        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Warning Center</div>
        <WarningCard
          w={demoState.demoWarning}
          onChanged={() => onPhase("reassign")}
          onAction={async () => ({})}
          onUiState={onPhase}
          reassignPreloaded={demoState.replacementPreloaded}
          onReassignSubmit={async () => ({ new_vehicle: { name: demoState.replacementVehicle.name } })}
        />
      </div>
    </StaffPreviewFrame>
  );
}

function ChangeReviewScreen({ demoState, onAdvance }) {
  return (
    <div data-testid="walkthrough-change-review-screen">
      <CustomerConfirmation preloaded={demoState.reconfirmPreloaded} onAction={() => ({ status: "awaiting_customer_confirmation" })} />
      <div className="max-w-3xl mt-4">
        <button type="button" onClick={onAdvance} data-testid="walkthrough-change-review-continue"
                className="inline-flex min-h-11 w-full sm:w-auto items-center justify-center gap-2 rounded-lg bg-[#1B4332] px-4 py-2.5 text-sm font-semibold text-white">
          Continue to Confirm Changes <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** The real My Booking page (CustomerBookingDetail): status still
 * Confirmed, risk_status "affected_action_needed", so the real RiskPill
 * ("Watch") and the real STATUS_EXPLAINERS copy ("Your booking is directly
 * affected by an incident. Please check the warning and contact the
 * business immediately.") render exactly as a renter would actually see
 * them. Replaces an earlier invented "simulated push notification" bubble -
 * this real page has no click target whose job is "move on to the next
 * beat of this walkthrough", so a small, clearly-separate Continue affordance
 * sits below the real page rather than pretending some real button serves
 * that purpose. */
function AtRiskScreen({ demoState, onAdvance }) {
  const [showCancellation, setShowCancellation] = useState(false);
  if (showCancellation) {
    return (
      <div data-testid="walkthrough-renter-cancellation-branch">
        <CustomerBookingDetail key="cancelled" preloaded={demoState.issueCancelledPreloaded} />
        <button type="button" onClick={() => setShowCancellation(false)} data-testid="walkthrough-renter-cancellation-back" className="mt-4 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">← Back to the successful branch</button>
      </div>
    );
  }
  return (
    <div data-testid="walkthrough-at-risk-screen">
      <CustomerBookingDetail key="at-risk" preloaded={demoState.atRiskPreloaded} onContactBusiness={onAdvance} />
      <button type="button" onClick={() => setShowCancellation(true)} data-testid="walkthrough-renter-no-replacement" className="mt-4 text-sm font-medium text-rose-700 underline">
        What if no suitable replacement is available?
      </button>
    </div>
  );
}

/** The real confirmation page again, now showing a real pending-change diff
 * (CustomerConfirmation's PendingChangeCard): same component as the happy
 * path's ConfirmationScreen, different preloaded data and onAction result. */
function RenterReviewsChangeScreen({ demoState, onAdvance }) {
  const [preloaded] = useState(demoState.reconfirmPreloaded);
  function onAction(action) {
    if (action === "accept_changes" || action === "confirm") {
      setTimeout(() => onAdvance(), 1200);
      return { status: "confirmed" };
    }
    if (action === "acknowledge_damages") return {};
    return { status: "change_requested" };
  }
  return (
    <div data-testid="walkthrough-renter-reviews-screen">
      <CustomerConfirmation preloaded={preloaded} onAction={onAction} />
    </div>
  );
}

export { resolveSpotlightTarget, resolveSubstepCopy };
